/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import type { TalerMerchantApi } from "@gnu-taler/taler-util";
import { HttpStatusCode, MerchantAuthMethod } from "@gnu-taler/taler-util";
import { useBusinessSettings } from "../api/hooks.js";
import { merchantClient } from "../api/client.js";
import {
  createPasswordVerifier,
  session,
  currentBackendUrl,
  currentAccount,
  setSessionPasswordVerifier,
  verifySessionPassword,
} from "../stores/session.js";
import { useTranslation } from "../context/translation.js";
import { BusinessSettingsScreen } from "../screens/BusinessSettingsScreen.js";
import type { PendingProtectedAction } from "../api/protectedAction.js";
import { runProtectedMutation } from "../api/contracts.js";
import {
  configurationFailure,
  normalizeApiFailure,
  unwrapEmpty,
} from "../api/failure.js";

export interface BusinessSettingsRouteProps {
  onMfaRequired: (pending: PendingProtectedAction) => void;
}

export function BusinessSettingsRoute({ onMfaRequired }: BusinessSettingsRouteProps): VNode {
  const { t } = useTranslation();
  const { settings, updateSettings, resource } = useBusinessSettings();
  const hasToken = Boolean(session.value.token);

  return (
    <BusinessSettingsScreen
      settings={hasToken ? settings : undefined}
      settingsResource={resource}
      passwordVerifierAvailable={Boolean(session.value.passwordVerifier)}
      onSave={async (newSettings) => {
        if (hasToken && updateSettings) {
          const res = await updateSettings(newSettings);
          if (res && res.mfaRequired && res.challengeResponse) {
            onMfaRequired({
              challengeAccount: currentAccount.value,
              challengeResponse:
                res.challengeResponse as TalerMerchantApi.ChallengeResponse,
              actionNotice: newSettings.name
                ? t`Updating business contact details (${newSettings.name})`
                : t`Updating merchant business contact details`,
              cancelTo: "/settings/account",
              continueWith: (challengeIds) =>
                runProtectedMutation(async () => {
                  const continued = await updateSettings(
                    newSettings,
                    challengeIds,
                  );
                  if (continued?.mfaRequired && continued.challengeResponse) {
                    return {
                      type: "challenge" as const,
                      challenge:
                        continued.challengeResponse as TalerMerchantApi.ChallengeResponse,
                    };
                  }
                  return { redirectTo: "/settings/account" };
                }),
              revalidate: resource.refresh,
            });
            return "challenge";
          }
        }
        return "saved";
      }}
      onChangePassword={async (data) => {
        if (hasToken) {
          try {
            const rootUrl = new URL(currentBackendUrl.value);
            const client = merchantClient({
              rootUrl,
              account: currentAccount.value,
            });
            if (session.value.passwordVerifier) {
              const matches = data.currentPassword
                ? await verifySessionPassword(data.currentPassword)
                : false;
              if (!matches) {
                return {
                  status: "error",
                  error: configurationFailure(
                    t`Your current password is not correct.`,
                  ),
                };
              }
            }
            // Prepare the replacement before mutating the server. If local
            // hashing fails, the form can report that without leaving the
            // merchant uncertain whether the backend changed the password.
            const nextVerifier = await createPasswordVerifier(data.newPassword);

            const changeRes = await client.updateCurrentInstanceAuthentication(
              session.value.token!,
              {
                method: MerchantAuthMethod.TOKEN,
                password: data.newPassword,
              },
            );
            if (changeRes.type === "fail") {
              const resCase = changeRes.case;
              if (resCase === HttpStatusCode.Accepted) {
                onMfaRequired({
                  challengeAccount: currentAccount.value,
                  actionNotice: t`Changing merchant account password`,
                  challengeResponse: changeRes.body as TalerMerchantApi.ChallengeResponse,
                  cancelTo: "/settings/account",
                  continueWith: (challengeIds) =>
                    runProtectedMutation(async () => {
                      const continued =
                        await client.updateCurrentInstanceAuthentication(
                          session.value.token!,
                          {
                            method: MerchantAuthMethod.TOKEN,
                            password: data.newPassword,
                          },
                          { challengeIds },
                        );
                      if (
                        continued.type === "fail" &&
                        continued.case === HttpStatusCode.Accepted
                      ) {
                        return {
                          type: "challenge" as const,
                          challenge:
                            continued.body as TalerMerchantApi.ChallengeResponse,
                        };
                      }
                      unwrapEmpty(continued);
                      setSessionPasswordVerifier(nextVerifier);
                      return { redirectTo: "/settings/account" };
                    }),
                });
                return { status: "challenge" };
              }
              return { status: "error", error: normalizeApiFailure(changeRes) };
            }
            setSessionPasswordVerifier(nextVerifier);
            return { status: "changed" };
          } catch (err: unknown) {
            return { status: "error", error: normalizeApiFailure(err) };
          }
        }
        return {
          status: "error",
          error: configurationFailure(t`Not authenticated.`),
        };
      }}
    />
  );
}
